import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useOrgCuratedCurrencies } from '../hooks/useOrgCuratedCurrencies';
import { formatCurrencyPickerLabel } from '../utils/iso4217';

interface AddCompanyFormData {
  name: string;
  companyNumber: string;
  description: string;
  currency: string;
}

export default function AddCompany() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { curatedCodes, orgDefaultCurrency } = useOrgCuratedCurrencies(organizationId);
  const [formData, setFormData] = useState<AddCompanyFormData>({
    name: '',
    companyNumber: '',
    description: '',
    currency: 'EUR'
  });

  useEffect(() => {
    if (!organizationId) {
      setError('Organization ID is missing.');
    }
  }, [organizationId]);

  useEffect(() => {
    if (curatedCodes.length === 0) return;
    setFormData((prev) => {
      const next =
        orgDefaultCurrency && curatedCodes.includes(orgDefaultCurrency)
          ? orgDefaultCurrency
          : curatedCodes.includes(prev.currency)
            ? prev.currency
            : curatedCodes[0];
      if (next === prev.currency) return prev;
      return { ...prev, currency: next };
    });
  }, [curatedCodes, orgDefaultCurrency]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!organizationId) {
      setError('Organization ID is missing.');
      return;
    }

    // Validate company name
    if (!formData.name || !formData.name.trim()) {
      setError('Please enter a company name.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const companyData = {
        organizationId,
        name: formData.name.trim(),
        ...(formData.companyNumber.trim()
          ? { companyNumber: formData.companyNumber.trim() }
          : {}),
        description: formData.description.trim() || 'Not specified',
        companyType: 'external',
        isActive: true,
        currency: formData.currency || curatedCodes[0] || 'EUR'
      };

      const response = await api.createCompany(companyData);
      
      if (response.success) {
        // Navigate back to organization view
        navigate(`/org/${organizationId}/manage`, { 
          state: { 
            message: 'Company created successfully',
            context: { type: 'organization', id: organizationId }
          }
        });
      } else {
        setError(response.message || 'Failed to create company');
      }
    } catch (err: any) {
      console.error('Error creating company:', err);
      setError(err.response?.data?.message || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/org/${organizationId}/manage`);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Add New Company
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Company Name */}
            <Grid item xs={12}>
              <TextField
                label="Company Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
                autoFocus
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Company number"
                value={formData.companyNumber}
                onChange={(e) => setFormData({ ...formData, companyNumber: e.target.value })}
                fullWidth
                disabled={loading}
                helperText="Optional reference code (not unique)"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={4}
                disabled={loading}
                helperText="Optional. If left empty, will default to 'Not specified'."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={loading || curatedCodes.length === 0}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  label="Currency"
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  {curatedCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {formatCurrencyPickerLabel(code)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ minWidth: 80, height: 32 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !formData.name.trim()}
                  sx={{ minWidth: 120, height: 32 }}
                >
                  Create Company
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
